import axios from 'axios';
import session from '../stores/session';

export const cancelTokens = new Map()

let authedInstance = null;
let guestInstance = null;

export default (authed = false) => {
    if (authed && authedInstance) return authedInstance;
    if (!authed && guestInstance) return guestInstance;

    let refreshPromise = null;
    let requestQueue = [];

    let obj = {
        baseURL: sessionStorage.getItem('api-path')
    };

    let ax = axios.create(obj);
    
    ax.interceptors.request.use(config => {
        const { url, doesCancel } = config

        const token = sessionStorage.getItem('user-token');

        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
        }
        
        if (doesCancel && cancelTokens.has(url)) {
            const cancel = cancelTokens.get(url)
            cancel()
            cancelTokens.delete(url)
        }
        
        const source = axios.CancelToken.source()
        config.cancelToken = source.token
        cancelTokens.set(url, () => {
            source.cancel()
            cancelTokens.delete(url)
        })

        if (config.params) {
            Object.entries(config.params).forEach(([key, val]) => {
              if (val !== null && typeof val === 'object') {
                config.params[key] = JSON.stringify(val)
              }
            })
          }
        
        return config
    }, err => {
        return Promise.reject(err)
    })

    ax.interceptors.response.use(
        res => {
            const { config } = res

            if (config.doesCancel) {
                cancelTokens.delete(config.url)
            }

            if (sessionStorage.getItem('maintenance_mode') === 'true') {
                sessionStorage.removeItem('maintenance_mode');
            }
            
            return res
        },
        async err => {
            console.log(err.response);

            if (axios.isCancel(err)) {
                // return Promise.reject({ status: 499, data: "Request cancelled" })
                return new Promise(() => {})
            }
            
            if (err.message === 'Network Error') {
                return Promise.reject({ status: 500, data: 'Network Error' });
            }

            if (err.response.data?.maintenance_mode) {
                const isMaintenancePage = window.location.pathname === '/maintenance';
                if (!isMaintenancePage) {
                    sessionStorage.setItem('maintenance_mode', 'true');
                    window.location.href = '/maintenance';
                }
                return Promise.reject(err.response);
            }

            if (err.response.status === 401 && err.response.data.message?.toLowerCase().includes('unauthenticated')) {
                const originalRequest = err.config || {};

                if (originalRequest._retry || originalRequest.url?.includes('/auth/refresh')) {
                    throw err;
                }

                originalRequest._retry = true;

                const retryRequest = new Promise((resolve, reject) => {
                    requestQueue.push({ resolve, reject, originalRequest });
                });

                if (!refreshPromise) {
                    refreshPromise = ax.post('/auth/refresh', {}, { withCredentials: true })
                        .then((response) => {
                            const { token } = response.data || {};
                            if (!token) throw new Error('No token in refresh response');
                            sessionStorage.setItem('user-token', token);
                            ax.defaults.headers.common.Authorization = `Bearer ${token}`;

                            requestQueue.forEach(({ resolve, originalRequest }) => {
                                originalRequest.headers = originalRequest.headers || {};
                                originalRequest.headers.Authorization = `Bearer ${token}`;
                                resolve(ax(originalRequest));
                            });

                            requestQueue.length = 0;
                            return token;
                        })
                        .catch((refreshErr) => {
                            requestQueue.forEach(({ reject }) => reject(refreshErr));
                            requestQueue.length = 0;

                            session.reset();
                            throw refreshErr;
                        })
                        .finally(() => {
                            refreshPromise = null;
                        });
                }

                return retryRequest;
            }

            if (err.response.status === 403 || err.response.status === 401) {
                console.log('Hit a 403, resetting session. bad 🗞')
                session.reset();
            }

            throw err;
        }
    )

    if (authed) {
        authedInstance = ax;
    } else {
        guestInstance = ax;
    }

    return ax;
}

export const cancelAllRequests = () => {
    cancelTokens.forEach((cancel) => cancel());
    cancelTokens.clear();
};
